import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { client } from "..";
import {
  WA_STATUS, WA_CONVERSATIONS, WA_CONVERSATION, WA_MESSAGES, WA_TEMPLATES, WA_QUICK_REPLIES, WA_ASSIGNEES, WA_MEDIA_LINKS,
} from "../Queries/WhatsApp";

const PAGE_SIZE = 30;
const MESSAGE_PAGE = 50;

const gqlError = (err) => err?.graphQLErrors?.[0]?.message || err?.message || "Something went wrong";

// ==================== THUNKS ====================

export const fetchWaStatus = createAsyncThunk("whatsapp/fetchStatus", async (_, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_STATUS, fetchPolicy: "network-only" });
    return data.whatsappStatus;
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

export const fetchWaConversations = createAsyncThunk(
  "whatsapp/fetchConversations",
  async ({ append = false } = {}, { getState, rejectWithValue }) => {
    const { filter, search, tag, list } = getState().whatsapp;
    const page = append ? list.page + 1 : 1;
    try {
      const { data } = await client.query({
        query: WA_CONVERSATIONS,
        variables: { filter, search: search || null, tag: tag || null, page, pageSize: PAGE_SIZE },
        fetchPolicy: "network-only",
      });
      return { ...data.whatsappConversations, append, requestKey: `${filter}|${search}|${tag}` };
    } catch (err) {
      return rejectWithValue(gqlError(err));
    }
  }
);

export const fetchWaConversation = createAsyncThunk("whatsapp/fetchConversation", async (id, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_CONVERSATION, variables: { id }, fetchPolicy: "network-only" });
    return { id, detail: data.whatsappConversation };
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

export const fetchWaMessages = createAsyncThunk(
  "whatsapp/fetchMessages",
  async ({ conversationId, before = null }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: WA_MESSAGES,
        variables: { conversationId, before, limit: MESSAGE_PAGE },
        fetchPolicy: "network-only",
      });
      return { conversationId, before, ...data.whatsappMessages };
    } catch (err) {
      return rejectWithValue(gqlError(err));
    }
  }
);

export const fetchWaTemplates = createAsyncThunk("whatsapp/fetchTemplates", async ({ refresh = false } = {}, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_TEMPLATES, variables: { refresh }, fetchPolicy: "network-only" });
    return data.whatsappTemplates || [];
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

export const fetchWaQuickReplies = createAsyncThunk("whatsapp/fetchQuickReplies", async (_, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_QUICK_REPLIES, fetchPolicy: "network-only" });
    return data.whatsappQuickReplies || [];
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

export const fetchWaAssignees = createAsyncThunk("whatsapp/fetchAssignees", async (_, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_ASSIGNEES, fetchPolicy: "network-only" });
    return data.whatsappAssignees || [];
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

/** Re-issue signed media links (they expire after an hour) without reloading the thread. */
export const refreshWaMediaLinks = createAsyncThunk("whatsapp/refreshMediaLinks", async (ids, { rejectWithValue }) => {
  try {
    const { data } = await client.query({ query: WA_MEDIA_LINKS, variables: { ids }, fetchPolicy: "network-only" });
    return data.whatsappMediaLinks || [];
  } catch (err) {
    return rejectWithValue(gqlError(err));
  }
});

// ==================== HELPERS ====================

const byLastMessage = (a, b) =>
  new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0);

/** Would this conversation appear under the current tab + search? */
export const matchesListFilter = (c, { filter, search, tag }, userId) => {
  if (!c) return false;
  if (tag && !(c.tags || []).includes(tag)) return false;
  const q = String(search || "").trim().toLowerCase();
  if (q) {
    const digits = q.replace(/\D/g, "");
    const hay = `${c.contactName || ""} ${c.profileName || ""} ${(c.tags || []).join(" ")}`.toLowerCase();
    if (!hay.includes(q) && !(digits.length >= 3 && String(c.waId).includes(digits))) return false;
  }
  switch (filter) {
    case "unassigned": return !c.assignedTo && c.status !== "closed";
    case "mine": return c.assignedTo === userId && c.status !== "closed";
    case "pending": return c.status === "pending";
    case "closed": return c.status === "closed";
    default: return c.status !== "closed";
  }
};

const initialState = {
  status: null,
  statusLoaded: false,
  statusError: null,
  filter: "all",
  search: "",
  tag: "",
  list: { items: [], page: 1, total: 0, hasMore: false, loading: false, error: null, requestKey: "" },
  activeId: null,
  details: {},      // conversationId → detail payload
  messages: {},     // conversationId → { items, hasMore, loading, error }
  templates: { items: [], loading: false, error: null, loaded: false },
  quickReplies: [],
  assignees: [],
  // "Message on WhatsApp" from a lead/client page → the inbox opens the New
  // message panel pre-filled with this recipient, then clears it.
  pendingNew: null,
};

const upsertMessageInto = (bucket, msg) => {
  const idx = bucket.items.findIndex((m) => m.id === msg.id);
  if (idx >= 0) bucket.items[idx] = { ...bucket.items[idx], ...msg };
  else {
    bucket.items.push(msg);
    bucket.items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }
};

// ==================== SLICE ====================

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    setWaFilter(state, action) {
      state.filter = action.payload;
    },
    setWaSearch(state, action) {
      state.search = action.payload;
    },
    setWaTag(state, action) {
      state.tag = action.payload;
    },
    setWaActive(state, action) {
      state.activeId = action.payload;
    },
    /** Insert/replace a conversation everywhere it is shown (socket + mutations). */
    upsertWaConversation(state, action) {
      const c = action.payload;
      if (!c?.id) return;
      const userId = state.status?.userId;
      const idx = state.list.items.findIndex((x) => x.id === c.id);
      const belongs = matchesListFilter(c, state, userId);
      if (belongs) {
        if (idx >= 0) state.list.items[idx] = c;
        else {
          state.list.items.push(c);
          state.list.total += 1;
        }
        state.list.items.sort(byLastMessage);
      } else if (idx >= 0) {
        state.list.items.splice(idx, 1);
        state.list.total = Math.max(0, state.list.total - 1);
      }
      if (state.details[c.id]) state.details[c.id].conversation = c;
    },
    removeWaConversation(state, action) {
      state.list.items = state.list.items.filter((x) => x.id !== action.payload);
    },
    /** Insert/replace a message in its thread (socket events + send results). */
    upsertWaMessage(state, action) {
      const msg = action.payload;
      if (!msg?.conversationId) return;
      const bucket = state.messages[msg.conversationId];
      if (!bucket) return; // thread not loaded — it will fetch fresh when opened
      upsertMessageInto(bucket, msg);
    },
    /** Status/media update for a message already in a thread. */
    patchWaMessage(state, action) {
      const msg = action.payload;
      const bucket = msg?.conversationId && state.messages[msg.conversationId];
      if (!bucket) return;
      const idx = bucket.items.findIndex((m) => m.id === msg.id);
      if (idx >= 0) bucket.items[idx] = { ...bucket.items[idx], ...msg };
    },
    setWaCounts(state, action) {
      if (state.status) state.status.counts = action.payload;
    },
    upsertWaQuickReply(state, action) {
      const q = action.payload;
      const idx = state.quickReplies.findIndex((x) => x.id === q.id);
      if (idx >= 0) state.quickReplies[idx] = q;
      else state.quickReplies.push(q);
    },
    removeWaQuickReply(state, action) {
      state.quickReplies = state.quickReplies.filter((x) => x.id !== action.payload);
    },
    requestWaNewConversation(state, action) {
      state.pendingNew = { ...action.payload, nonce: Date.now() };
    },
    clearWaPendingNew(state) {
      state.pendingNew = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshWaMediaLinks.fulfilled, (state, action) => {
        const urls = new Map(action.payload.map((l) => [l.id, l.url]));
        if (!urls.size) return;
        Object.values(state.messages).forEach((bucket) => {
          bucket.items.forEach((m) => {
            if (m.media && urls.has(m.id)) m.media.url = urls.get(m.id);
          });
        });
      })
      .addCase(fetchWaStatus.fulfilled, (state, action) => {
        state.status = action.payload;
        state.statusLoaded = true;
        state.statusError = null;
      })
      .addCase(fetchWaStatus.rejected, (state, action) => {
        state.statusLoaded = true;
        state.statusError = action.payload || "Could not load WhatsApp status";
      })
      .addCase(fetchWaConversations.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchWaConversations.fulfilled, (state, action) => {
        const { items, page, total, hasMore, append, requestKey } = action.payload;
        // Ignore a slow response for a tab/search the user already left.
        if (requestKey !== `${state.filter}|${state.search}|${state.tag}`) return;
        state.list.loading = false;
        state.list.requestKey = requestKey;
        state.list.page = page;
        state.list.total = total;
        state.list.hasMore = hasMore;
        if (append) {
          const seen = new Set(state.list.items.map((c) => c.id));
          state.list.items.push(...items.filter((c) => !seen.has(c.id)));
        } else {
          state.list.items = items;
        }
      })
      .addCase(fetchWaConversations.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload || "Could not load conversations";
      })
      .addCase(fetchWaConversation.fulfilled, (state, action) => {
        state.details[action.payload.id] = action.payload.detail;
      })
      .addCase(fetchWaConversation.rejected, (state, action) => {
        const id = action.meta.arg;
        state.details[id] = { ...(state.details[id] || {}), error: action.payload };
      })
      .addCase(fetchWaMessages.pending, (state, action) => {
        const id = action.meta.arg.conversationId;
        if (!state.messages[id]) state.messages[id] = { items: [], hasMore: false, loading: true, error: null };
        else {
          state.messages[id].loading = true;
          state.messages[id].error = null;
        }
      })
      .addCase(fetchWaMessages.fulfilled, (state, action) => {
        const { conversationId, before, items, hasMore } = action.payload;
        const bucket = state.messages[conversationId];
        bucket.loading = false;
        bucket.hasMore = hasMore;
        if (before) {
          const seen = new Set(bucket.items.map((m) => m.id));
          bucket.items = [...items.filter((m) => !seen.has(m.id)), ...bucket.items];
        } else {
          // Keep any live messages that arrived while the page was loading.
          const fresh = new Set(items.map((m) => m.id));
          const live = bucket.items.filter((m) => !fresh.has(m.id) && new Date(m.timestamp) > new Date(items[items.length - 1]?.timestamp || 0));
          bucket.items = [...items, ...live];
        }
      })
      .addCase(fetchWaMessages.rejected, (state, action) => {
        const id = action.meta.arg.conversationId;
        if (state.messages[id]) {
          state.messages[id].loading = false;
          state.messages[id].error = action.payload || "Could not load messages";
        }
      })
      .addCase(fetchWaTemplates.pending, (state) => {
        state.templates.loading = true;
        state.templates.error = null;
      })
      .addCase(fetchWaTemplates.fulfilled, (state, action) => {
        state.templates = { items: action.payload, loading: false, error: null, loaded: true };
      })
      .addCase(fetchWaTemplates.rejected, (state, action) => {
        state.templates.loading = false;
        state.templates.error = action.payload || "Could not load templates";
      })
      .addCase(fetchWaQuickReplies.fulfilled, (state, action) => {
        state.quickReplies = action.payload;
      })
      .addCase(fetchWaAssignees.fulfilled, (state, action) => {
        state.assignees = action.payload;
      });
  },
});

export const {
  setWaFilter, setWaSearch, setWaTag, setWaActive, upsertWaConversation, removeWaConversation,
  upsertWaMessage, patchWaMessage, setWaCounts, upsertWaQuickReply, removeWaQuickReply,
  requestWaNewConversation, clearWaPendingNew,
} = whatsappSlice.actions;

export default whatsappSlice.reducer;
